/**
 * The Super Admin console — SRS §9.2 / screens 8–17. Portal requests, portals,
 * plans, subscriptions & payments, the platform trace log, the mail outbox and
 * reports. Every screen is a fetch-on-mount view over `platform.sa.*`; every
 * mutation refetches — this console is used by ONE person and correctness
 * beats cleverness.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, Outlet, useNavigate, useParams } from 'react-router-dom';
import { platform, uploadImage } from '../../lib/api';
import { formatDate, formatMoney, today } from '../../data';
import { pickAndResizePhoto, QR_IMAGE } from '../../lib/image';
import { ConsoleShell } from '../../components/ConsoleShell';
import { BackToClose } from '../../hooks/useBackToClose';
import {
  Button,
  Empty,
  Eyebrow,
  Field,
  FormError,
  Hint,
  INPUT_CLS,
  Pill,
  Row2,
  Spinner,
  Stat,
  Stats,
  Table,
  TEXTAREA_CLS,
} from '../../components/consoleUi';

const SA_TABS = [
  ['/sa/dashboard', 'Dashboard', 'grid'],
  ['/sa/requests', 'Requests', 'plus'],
  ['/sa/portals', 'Portals', 'folder'],
  ['/sa/plans', 'Plans', 'bib'],
  ['/sa/payments', 'Payments', 'rupee'],
  ['/sa/bank', 'Bank details', 'gear'],
  ['/sa/trace', 'Trace', 'eye'],
  ['/sa/emails', 'Mail', 'mail'],
  ['/sa/reports', 'Reports', 'stopwatch'],
];


/** Table head from labels — the Table atom expects a <tr>, not strings. */
function th(cols) {
  return (
    <tr>
      {cols.map((c, i) => (
        <th key={i}>{c}</th>
      ))}
    </tr>
  );
}

export function SAShell() {
  // No title → the shell shows the RUNNER·CREW platform mark.
  return (
    <ConsoleShell subtitle="SUPER ADMIN" tabs={SA_TABS}>
      <Outlet />
    </ConsoleShell>
  );
}

/** Fetch-on-mount with a reload handle. */
function useLoad(fn, deps = []) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const reload = useCallback(() => {
    setError(null);
    fn().then(setData).catch((err) => setError(err?.message ?? 'Could not load.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => {
    reload();
  }, [reload]);
  return { data, error, reload };
}

function LoadGate({ state, children }) {
  if (state.error) {
    return (
      <Empty title="Could not load">
        {state.error} <button type="button" className="underline" onClick={state.reload}>Try again</button>
      </Empty>
    );
  }
  if (!state.data) return <div className="flex justify-center py-16"><Spinner /></div>;
  return children(state.data);
}

const ACCESS_TONE = { active: 'ok', grace: 'soon', blocked: 'late', suspended: 'late' };
const PAYMENT_TONE = { requested: 'pend', link_sent: 'sent', paid: 'soon', verified: 'ok', failed: 'late', cancelled: 'grey' };
/* 'paid' is what the PAYER says; only 'verified' is what the bank says. The
   label keeps the two apart on every row. */
const PAYMENT_LABEL = {
  requested: 'requested',
  link_sent: 'link sent',
  paid: 'awaiting verification',
  verified: 'verified',
  failed: 'rejected',
  cancelled: 'withdrawn',
};

/* ------------------------------------------------------------ dashboard -- */

export function SADashboard() {
  const navigate = useNavigate();
  const state = useLoad(() => platform.sa.overview());
  return (
    <LoadGate state={state}>
      {(d) => (
        <>
          <Eyebrow>Platform</Eyebrow>
          <Stats>
            <Stat value={d.activePortals} label="Active portals" />
            <Stat
              value={d.awaitingVerification ?? 0}
              label="Payments to verify"
              numberTone={d.awaitingVerification ? 'flag' : undefined}
              onClick={() => navigate('/sa/payments')}
            />
            <Stat
              value={d.newEnquiries ?? 0}
              label="Sales enquiries"
              numberTone={d.newEnquiries ? 'flag' : undefined}
            />
            <Stat value={formatMoney(d.totalRevenue)} label="Verified revenue" />
            <Stat value={d.mailFailures} label="Mail failures" numberTone={d.mailFailures ? 'flag' : undefined} />
          </Stats>

          <div className="mt-8">
            <Eyebrow>Expiring within a month</Eyebrow>
            {d.upcomingExpiries.length === 0 ? (
              <Empty title="Nothing expiring soon" />
            ) : (
              <Table head={th(['Portal', 'Ends'])}>
                {d.upcomingExpiries.map((e) => (
                  <tr key={e.portalId}>
                    <td>
                      <Link className="font-bold text-sea-600 underline decoration-sea-600/30 underline-offset-[3px]" to={`/sa/portals/${e.portalId}`}>
                        {e.portalName}
                      </Link>
                    </td>
                    <td className="font-market tabular-nums">{formatDate(e.endDate)}</td>
                  </tr>
                ))}
              </Table>
            )}
          </div>
        </>
      )}
    </LoadGate>
  );
}

/* ------------------------------------------------------------- requests -- */

export function SARequests() {
  const state = useLoad(() => platform.sa.requests());
  const [action, setAction] = useState(null); // { kind: 'link'|'reject', request }
  const [value, setValue] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  async function run() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      if (action.kind === 'link') {
        await platform.sa.sendRequestPaymentLink(action.request.id, Number(value) || undefined);
      } else {
        if (!value.trim()) throw new Error('A rejection reason is mandatory.');
        await platform.sa.rejectRequest(action.request.id, value.trim());
      }
      setAction(null);
      setValue('');
      state.reload();
    } catch (err) {
      setError(err?.message ?? 'Could not do that.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <LoadGate state={state}>
      {(d) => (
        <>
          <Eyebrow right={d.requests.length}>Portal requests</Eyebrow>
          {d.requests.length === 0 ? (
            <Empty title="No requests yet" />
          ) : (
            <div className="grid gap-px border border-line bg-line">
              {d.requests.map((r) => (
                <div key={r.id} className="bg-white p-5">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="font-market text-[17px] font-extrabold tracking-[-0.02em] text-ink-700">{r.portalName}</p>
                      <p className="text-sm text-copy-muted">
                        {r.name} · <span className="font-market">{r.email}</span>
                        {r.phone ? <> · <span className="font-market">{r.phone}</span></> : null}
                      </p>
                      <p className="mt-1 text-xs text-copy-label">
                        {r.plan.name} — {r.plan.seats} seats · {formatMoney(r.plan.price)}/yr
                        {r.address ? ` · ${r.address}` : ''}
                      </p>
                      {r.description && <p className="mt-1 text-xs italic text-copy-label">“{r.description}”</p>}
                      {r.status === 'rejected' && r.declineReason && (
                        <p className="mt-1 text-xs text-flag-600">Rejected: {r.declineReason}</p>
                      )}
                    </div>
                    <Pill tone={{ pending: 'pend', payment_pending: 'sent', approved: 'ok', rejected: 'late' }[r.status]}>
                      {r.status.replace('_', ' ')}
                    </Pill>
                  </div>
                  {(r.status === 'pending' || r.status === 'payment_pending') && (
                    <div className="mt-3 flex gap-2">
                      <Button
                        small
                        tone="p"
                        onClick={() => {
                          setAction({ kind: 'link', request: r });
                          setValue(String(r.plan.price));
                          setError(null);
                        }}
                      >
                        {r.status === 'pending' ? 'Approve · send payment link' : 'Resend payment link'}
                      </Button>
                      <Button
                        small
                        tone="r"
                        onClick={() => {
                          setAction({ kind: 'reject', request: r });
                          setValue('');
                          setError(null);
                        }}
                      >
                        Reject
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {action && (
            <div className="fixed inset-0 z-50 grid place-items-center bg-ink-900/70 p-4 backdrop-blur-[2px] motion-safe:animate-fadein" role="dialog">
              <BackToClose onClose={() => setAction(null)} />
              <div className="w-full max-w-sm border border-line bg-white p-5 shadow-[0_40px_80px_-40px_rgba(11,15,24,.55)]">
                <p className="font-market text-[17px] font-extrabold tracking-[-0.02em] text-ink-700">
                  {action.kind === 'link' ? 'Send payment link' : 'Reject request'}
                </p>
                <p className="mb-4 mt-1 text-sm text-copy-muted">{action.request.portalName}</p>
                {action.kind === 'link' ? (
                  <Field label="Amount (₹)" required>
                    <input className={INPUT_CLS} type="number" value={value} onChange={(e) => setValue(e.target.value)} />
                  </Field>
                ) : (
                  <Field label="Reason — the applicant reads this" required>
                    <textarea className={TEXTAREA_CLS} value={value} onChange={(e) => setValue(e.target.value)} />
                  </Field>
                )}
                {error && <FormError>{error}</FormError>}
                <div className="mt-4 flex justify-end gap-2">
                  <Button small onClick={() => setAction(null)}>Cancel</Button>
                  <Button small tone={action.kind === 'link' ? 'p' : 'r'} onClick={run} disabled={busy}>
                    {busy ? 'Working…' : action.kind === 'link' ? 'Send link' : 'Reject'}
                  </Button>
                </div>
              </div>
            </div>
          )}
          <SalesEnquiries />
        </>
      )}
    </LoadGate>
  );
}

/**
 * "Talk to sales" from the marketing site — people who want a conversation
 * before they pick a plan.
 *
 * It lives on the Requests screen rather than a tab of its own because it is
 * the same job: somebody outside the platform is waiting for the owner to get
 * back to them. The only action is marking whether that has happened; the call
 * itself is a phone call, and if it goes well the outcome arrives above as a
 * portal request.
 */
/* A small button that is really a link — tel: and mailto: need an anchor. */
const CONTACT_BTN =
  'inline-flex items-center px-3.5 py-2 font-market text-[12.5px] font-extrabold text-ink-700 border border-line-strong bg-white transition-colors duration-200 hover:border-ink-700';

function SalesEnquiries() {
  const state = useLoad(() => platform.sa.salesEnquiries());
  const [busyId, setBusyId] = useState(null);

  async function mark(id, status) {
    setBusyId(id);
    try {
      await platform.sa.setEnquiryStatus(id, status);
      state.reload();
    } finally {
      setBusyId(null);
    }
  }

  if (state.error || !state.data) return null;
  const list = state.data.enquiries ?? [];

  return (
    <div className="mt-8">
      <Eyebrow right={list.filter((e) => e.status === 'new').length || null}>
        Sales enquiries
      </Eyebrow>
      {list.length === 0 ? (
        <Empty title="No enquiries yet">
          Anyone who taps “Talk to sales” on the public site lands here.
        </Empty>
      ) : (
        <div className="grid gap-px border border-line bg-line">
          {list.map((e) => (
            <div key={e.id} className="bg-white p-5">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-market text-[17px] font-extrabold tracking-[-0.02em] text-ink-700">
                    {e.orgName}
                    {e.memberCount ? (
                      <span className="ml-2 font-market text-[13px] font-semibold tabular-nums text-copy-label">
                        ~{e.memberCount} members
                      </span>
                    ) : null}
                  </p>
                  <p className="text-sm text-copy-muted">
                    {e.name} · <span className="font-market">{e.email}</span>
                    {e.phone ? <> · <span className="font-market tabular-nums">{e.phone}</span></> : null}
                  </p>
                  {e.message && <p className="mt-1 text-xs italic text-copy-label">“{e.message}”</p>}
                  <p className="mt-1 font-market text-[10.5px] uppercase tracking-[0.14em] text-copy-label">
                    {formatDate(String(e.createdAt).slice(0, 10))}
                    {e.handledBy ? ` · ${e.status} by ${e.handledBy}` : ''}
                  </p>
                </div>
                <Pill tone={{ new: 'pend', contacted: 'sent', closed: 'ok' }[e.status]}>
                  {e.status}
                </Pill>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {/* Anchors, not buttons: window.open on a tel:/mailto: URL
                    leaves an empty tab behind on a desktop browser. */}
                {e.phone && (
                  <a href={`tel:${e.phone}`} className={CONTACT_BTN}>
                    Call
                  </a>
                )}
                <a href={`mailto:${e.email}`} className={CONTACT_BTN}>
                  Email
                </a>
                {e.status !== 'contacted' && (
                  <Button small disabled={busyId === e.id} onClick={() => mark(e.id, 'contacted')}>
                    Mark contacted
                  </Button>
                )}
                {e.status !== 'closed' && (
                  <Button small disabled={busyId === e.id} onClick={() => mark(e.id, 'closed')}>
                    Close
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------- portals -- */

/**
 * Setting a club up by hand — the Super Admin's twin of a committee's
 * "Add member" (owner's direction, 13 Sep 2026).
 *
 * The self-service road is: they register, start a trial, pick a plan, pay,
 * upload a screenshot. This is the other one: they talked to you, gave you
 * the money, and expect their portal to be there. One dialog creates the
 * owner account (with an invitation to set a password), the portal, the
 * subscription and the already-settled payment.
 *
 * Amount is pre-filled from the plan and may be zeroed — a portal comped for
 * a partner club is a real thing, and ₹0 with no method is the honest record
 * of it.
 */
function AddPortalDialog({ onClose, onDone }) {
  const plans = useLoad(() => platform.sa.plans());
  const [form, setForm] = useState({
    portalName: '',
    ownerName: '',
    email: '',
    phone: '',
    address: '',
    planId: '',
    amount: '',
    method: 'cash',
    date: today(),
    reference: '',
  });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const planList = (plans.data?.plans ?? []).filter((p) => p.status === 'active' && !p.isTrial);
  const chosen = planList.find((p) => p.id === form.planId) ?? null;

  function update(patch) {
    setForm((f) => {
      const next = { ...f, ...patch };
      // Picking a plan quotes its price; typing over it is expected.
      if ('planId' in patch) {
        const plan = planList.find((p) => p.id === next.planId);
        next.amount = plan ? String(plan.price) : '';
      }
      return next;
    });
  }

  async function save() {
    if (busy) return;
    if (!form.portalName.trim()) return setError('Enter the club or organisation name.');
    if (!form.ownerName.trim()) return setError("Enter the owner's name.");
    if (!form.email.trim()) return setError('Enter their email — it is how they sign in.');
    if (!form.phone.trim()) return setError('Enter their mobile number.');
    if (!form.planId) return setError('Choose a plan.');
    const amount = Number(form.amount || 0);
    if (!(amount >= 0)) return setError('Enter the amount you received.');
    setBusy(true);
    setError(null);
    try {
      const res = await platform.sa.createPortal({
        portalName: form.portalName.trim(),
        ownerName: form.ownerName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        address: form.address.trim() || undefined,
        planId: form.planId,
        amount,
        method: amount > 0 ? form.method : undefined,
        date: form.date,
        reference: form.reference.trim() || undefined,
      });
      onDone(res.portalId);
    } catch (err) {
      setError(err?.message ?? 'Could not create that portal.');
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink-900/70 p-4 backdrop-blur-[2px] motion-safe:animate-fadein" role="dialog">
      <BackToClose onClose={onClose} />
      <div className="max-h-[92vh] w-full max-w-md overflow-auto border border-line bg-white p-5 shadow-[0_40px_80px_-40px_rgba(11,15,24,.55)]">
        <p className="font-market text-[17px] font-extrabold tracking-[-0.02em] text-ink-700">
          Add an admin portal
        </p>
        <p className="mb-4 mt-1 text-sm leading-relaxed text-copy-muted">
          For a club you signed up yourself. The portal goes live at once and the owner is emailed
          an invitation to set their password.
        </p>

        <Field label="Club / organisation" required>
          <input
            className={INPUT_CLS}
            value={form.portalName}
            onChange={(e) => update({ portalName: e.target.value })}
            placeholder="e.g. Erode Runners"
          />
        </Field>
        <Field label="Owner's name" required>
          <input
            className={INPUT_CLS}
            value={form.ownerName}
            onChange={(e) => update({ ownerName: e.target.value })}
            placeholder="Who runs it"
          />
        </Field>
        <Row2>
          <Field label="Email" required>
            <input
              className={INPUT_CLS}
              type="email"
              value={form.email}
              onChange={(e) => update({ email: e.target.value })}
              placeholder="name@example.com"
            />
          </Field>
          <Field label="Mobile" required>
            <input
              className={INPUT_CLS}
              value={form.phone}
              onChange={(e) => update({ phone: e.target.value })}
              placeholder="98765 43210"
            />
          </Field>
        </Row2>
        <Field label="Address">
          <input
            className={INPUT_CLS}
            value={form.address}
            onChange={(e) => update({ address: e.target.value })}
            placeholder="Optional — city is enough"
          />
        </Field>

        <Field label="Plan" required>
          <select
            className={INPUT_CLS}
            value={form.planId}
            onChange={(e) => update({ planId: e.target.value })}
          >
            <option value="">Choose…</option>
            {planList.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} — {p.seats} seats · {formatMoney(p.price)}
              </option>
            ))}
          </select>
          {chosen && (
            <Hint>
              {chosen.seats} seats for {chosen.months} months from today.
            </Hint>
          )}
        </Field>

        <Row2>
          <Field label="Amount received (₹)" required>
            <input
              className={INPUT_CLS}
              type="number"
              value={form.amount}
              onChange={(e) => update({ amount: e.target.value })}
            />
          </Field>
          <Field label="How">
            <select
              className={INPUT_CLS}
              value={form.method}
              disabled={Number(form.amount || 0) <= 0}
              onChange={(e) => update({ method: e.target.value })}
            >
              {METHODS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
        </Row2>
        <Row2>
          <Field label="Received on">
            <input
              className={INPUT_CLS}
              type="date"
              max={today()}
              value={form.date}
              onChange={(e) => update({ date: e.target.value })}
            />
          </Field>
          <Field label="Reference">
            <input
              className={INPUT_CLS}
              value={form.reference}
              onChange={(e) => update({ reference: e.target.value })}
              placeholder="UTR / note"
            />
          </Field>
        </Row2>
        {Number(form.amount || 0) === 0 && (
          <Hint>
            ₹0 records a comped portal — the plan still starts today, and the payment row says
            nothing was collected.
          </Hint>
        )}

        {error && <FormError>{error}</FormError>}
        <div className="mt-4 flex justify-end gap-2">
          <Button small onClick={onClose}>Cancel</Button>
          <Button small tone="p" disabled={busy} onClick={save}>
            {busy ? 'Creating…' : 'Create portal'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function SAPortals() {
  const navigate = useNavigate();
  const state = useLoad(() => platform.sa.portals());
  const [adding, setAdding] = useState(false);
  return (
    <LoadGate state={state}>
      {(d) => (
        <>
          <Eyebrow right={d.portals.length}>Admin portals</Eyebrow>
          <p className="mb-3 text-xs text-copy-label">
            Clubs that signed up online land here on their own. Use{' '}
            <b>Add admin portal</b> for one you set up yourself — it creates the
            owner&rsquo;s account, emails them an invitation, and records what they paid you.
          </p>
          <div className="mb-3">
            <Button small tone="p" onClick={() => setAdding(true)}>
              + Add admin portal
            </Button>
          </div>
          {adding && (
            <AddPortalDialog
              onClose={() => setAdding(false)}
              onDone={(portalId) => {
                setAdding(false);
                if (portalId) navigate(`/sa/portals/${portalId}`);
                else state.reload();
              }}
            />
          )}
          {d.portals.length === 0 ? (
            <Empty title="No portals yet">
              Add the first one, or wait for a club to register on the site.
            </Empty>
          ) : (
            <Table head={th(['Portal', 'Owner', 'Plan', 'Seats', 'Clubs', 'Ends', 'State'])}>
              {d.portals.map((p) => (
                <tr key={p.id}>
                  <td>
                    <Link className="font-bold text-sea-600 underline decoration-sea-600/30 underline-offset-[3px]" to={`/sa/portals/${p.id}`}>
                      {p.name}
                    </Link>
                  </td>
                  <td className="text-copy-muted">{p.owner.name}</td>
                  <td>{p.plan ?? '—'}</td>
                  <td className="font-market tabular-nums">{p.usedSeats}/{p.capacity}</td>
                  <td className="font-market tabular-nums">{p.clubCount}</td>
                  <td className="font-market tabular-nums">{p.endDate ? formatDate(p.endDate) : '—'}</td>
                  <td><Pill tone={ACCESS_TONE[p.accessState]}>{p.status === 'suspended' ? 'suspended' : p.accessState}</Pill></td>
                </tr>
              ))}
            </Table>
          )}
        </>
      )}
    </LoadGate>
  );
}

export function SAPortalDetail() {
  const { portalId } = useParams();
  const navigate = useNavigate();
  const state = useLoad(() => platform.sa.portalDetail(portalId), [portalId]);
  const [busy, setBusy] = useState(false);
  const [linkForm, setLinkForm] = useState(null); // { type, planId, amount }
  const [recording, setRecording] = useState(false); // "they paid me directly" 
  // A native prompt() here froze the whole tab in testing — the reason gets a
  // proper dialog, and the owner-reads-this warning moves into it.
  const [suspending, setSuspending] = useState(null); // { reason }
  const [error, setError] = useState(null);

  async function act(fn) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await fn();
      setSuspending(null);
      state.reload();
    } catch (err) {
      setError(err?.message ?? 'Could not do that.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <LoadGate state={state}>
      {(d) => (
        <>
          <button type="button" className="mb-3 font-market text-xs uppercase text-copy-label hover:text-ink-700" onClick={() => navigate('/sa/portals')}>
            ← All portals
          </button>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="font-market text-[30px] font-extrabold tracking-[-0.032em] text-ink-700">{d.portal.name}</h1>
              <p className="text-sm text-copy-muted">
                {d.portal.owner.name} · <span className="font-market">{d.portal.owner.email}</span>
              </p>
              {d.portal.status === 'suspended' && (
                <p className="mt-1 text-sm text-flag-600">
                  Suspended{d.portal.suspensionReason ? ` — ${d.portal.suspensionReason}` : ''}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              {d.portal.status === 'suspended' ? (
                <Button small tone="p" disabled={busy} onClick={() => act(() => platform.sa.reactivatePortal(portalId))}>
                  Reactivate
                </Button>
              ) : (
                <Button
                  small
                  tone="r"
                  disabled={busy}
                  onClick={() => { setSuspending({ reason: '' }); setError(null); }}
                >
                  Suspend
                </Button>
              )}
              <Button small tone="d" onClick={() => setLinkForm({ type: 'renewal', planId: '', amount: '' })}>
                Send payment link
              </Button>
              {/* The other half of getting paid: money that already reached
                  you, with no link and no screenshot in it anywhere. */}
              <Button small tone="p" onClick={() => { setRecording(true); setError(null); }}>
                Record payment
              </Button>
            </div>
          </div>
          {error && <FormError>{error}</FormError>}

          <Stats style={{ marginTop: 20 }}>
            <Stat value={d.portal.usedSeats} label="Seats in use" />
            <Stat value={d.clubs.filter((c) => c.status === 'active').length} label="Active clubs" />
            <Stat value={d.payments.filter((p) => p.status === 'verified').length} label="Verified payments" />
          </Stats>

          <div className="mt-8">
            <Eyebrow>Subscription history</Eyebrow>
            <Table head={th(['Plan', 'Seats', 'Term', 'Type', 'Status'])}>
              {d.subscriptions.map((s) => (
                <tr key={s.id}>
                  <td>{s.plan}</td>
                  <td className="font-market tabular-nums">{s.capacity}</td>
                  <td className="font-market tabular-nums">{formatDate(s.start)} → {formatDate(s.end)}</td>
                  <td>{s.changeType}</td>
                  <td><Pill tone={s.status === 'active' ? 'ok' : 'grey'}>{s.status}</Pill></td>
                </tr>
              ))}
            </Table>
          </div>

          <div className="mt-8">
            <Eyebrow>Clubs</Eyebrow>
            <Table head={th(['Club', 'Members', 'Status'])}>
              {d.clubs.map((c) => (
                <tr key={c.id}>
                  <td className="font-semibold text-ink-700">{c.name}</td>
                  <td className="font-market tabular-nums">{c.members}</td>
                  <td><Pill tone={c.status === 'active' ? 'ok' : 'grey'}>{c.status}</Pill></td>
                </tr>
              ))}
            </Table>
          </div>

          <div className="mt-8">
            <Eyebrow>Platform payments</Eyebrow>
            {d.payments.length === 0 ? (
              <Empty title="No payments yet" />
            ) : (
              <Table head={th(['Type', 'Amount', 'Status', 'Invoice', 'Date'])}>
                {d.payments.map((p) => (
                  <tr key={p.id}>
                    <td>{p.type}</td>
                    <td className="font-market tabular-nums">{formatMoney(p.amount)}</td>
                    <td><Pill tone={PAYMENT_TONE[p.status]}>{p.status.replace('_', ' ')}</Pill></td>
                    <td className="font-market">{p.invoiceNumber ?? '—'}</td>
                    <td className="font-market tabular-nums">{p.date ? formatDate(p.date) : '—'}</td>
                  </tr>
                ))}
              </Table>
            )}
          </div>

          {suspending && (
            <div className="fixed inset-0 z-50 grid place-items-center bg-ink-900/70 p-4 backdrop-blur-[2px] motion-safe:animate-fadein" role="dialog">
              <BackToClose onClose={() => setSuspending(null)} />
              <div className="w-full max-w-sm border border-line bg-white p-5 shadow-[0_40px_80px_-40px_rgba(11,15,24,.55)]">
                <p className="font-market text-[17px] font-extrabold tracking-[-0.02em] text-ink-700">
                  Suspend {d.portal.name}?
                </p>
                <p className="mb-4 mt-1 text-sm leading-relaxed text-copy-muted">
                  The portal's admin sees a notice; members see their club as
                  unavailable. The reason below is emailed to the owner.
                </p>
                <Field label="Suspension reason">
                  <textarea
                    className={TEXTAREA_CLS}
                    value={suspending.reason}
                    onChange={(e) => setSuspending((s) => ({ ...s, reason: e.target.value }))}
                  />
                </Field>
                {error && <FormError>{error}</FormError>}
                <div className="mt-4 flex justify-end gap-2">
                  <Button small onClick={() => setSuspending(null)}>Cancel</Button>
                  <Button
                    small
                    tone="r"
                    disabled={busy}
                    onClick={() => act(() => platform.sa.suspendPortal(portalId, suspending.reason))}
                  >
                    {busy ? 'Suspending…' : 'Suspend portal'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {recording && (
            <RecordPaymentDialog
              portalId={portalId}
              portalName={d.portal.name}
              currentPlan={d.subscriptions.find((x) => x.status === 'active')?.plan ?? null}
              onClose={() => setRecording(false)}
              onDone={() => {
                setRecording(false);
                state.reload();
              }}
            />
          )}

          {linkForm && (
            <PaymentLinkDialog
              portalId={portalId}
              form={linkForm}
              setForm={setLinkForm}
              onDone={() => {
                setLinkForm(null);
                state.reload();
              }}
            />
          )}
        </>
      )}
    </LoadGate>
  );
}

function PaymentLinkDialog({ portalId, form, setForm, onDone }) {
  const plans = useLoad(() => platform.sa.plans());
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  async function send() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await platform.sa.sendPortalPaymentLink(
        portalId,
        form.type,
        form.type === 'upgrade' ? form.planId : undefined,
        Number(form.amount),
      );
      onDone();
    } catch (err) {
      setError(err?.message ?? 'Could not send the link.');
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink-900/70 p-4 backdrop-blur-[2px] motion-safe:animate-fadein" role="dialog">
      <BackToClose onClose={() => setForm(null)} />
      <div className="w-full max-w-sm border border-line bg-white p-5 shadow-[0_40px_80px_-40px_rgba(11,15,24,.55)]">
        <p className="mb-4 font-market text-[17px] font-extrabold tracking-[-0.02em] text-ink-700">Payment link</p>
        <Field label="What for" required>
          <div className="flex gap-2">
            {['renewal', 'upgrade'].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setForm((f) => ({ ...f, type: t }))}
                className={`flex-1 border px-3 py-2 font-market text-xs uppercase ${
                  form.type === t ? 'border-gold-500 bg-gold-50' : 'border-line-strong'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </Field>
        {form.type === 'upgrade' && (
          <Field label="Target plan" required>
            <select
              className={INPUT_CLS}
              value={form.planId}
              onChange={(e) => setForm((f) => ({ ...f, planId: e.target.value }))}
            >
              <option value="">Choose…</option>
              {(plans.data?.plans ?? [])
                .filter((p) => p.status === 'active' && !p.isTrial)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} — {p.seats} seats
                  </option>
                ))}
            </select>
          </Field>
        )}
        <Field label="Amount (₹)" required>
          <input
            className={INPUT_CLS}
            type="number"
            value={form.amount}
            onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
          />
        </Field>
        {error && <FormError>{error}</FormError>}
        <div className="mt-4 flex justify-end gap-2">
          <Button small onClick={() => setForm(null)}>Cancel</Button>
          <Button small tone="p" onClick={send} disabled={busy}>
            {busy ? 'Sending…' : 'Send link'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------- money received by hand -- */

/** How the money arrived. The club side's vocabulary, on the platform's books. */
const METHODS = [
  ['cash', 'Cash'],
  ['upi', 'UPI'],
  ['bank', 'Bank transfer'],
];
const METHOD_LABEL = Object.fromEntries(METHODS);

/**
 * Recording money that reached you OUTSIDE the app — notes handed over at a
 * meeting, or a transfer already sitting in your statement. The platform twin
 * of a club committee recording a member's cash payment.
 *
 * It books the payment AND applies the plan in one call: the person filling
 * this in is the person who took the money, so there is no screenshot to check
 * and nothing to verify afterwards. The portal owner gets the same "payment
 * verified" email they would have got the long way round.
 *
 * `portalId` fixes it to one portal (the Portal screen); without it the dialog
 * asks which portal paid (the Payments screen).
 */
function RecordPaymentDialog({ portalId, portalName, currentPlan, onDone, onClose }) {
  const plans = useLoad(() => platform.sa.plans());
  // Only the Payments screen needs the picker; the Portal screen already knows.
  const portals = useLoad(() => (portalId ? Promise.resolve(null) : platform.sa.portals()), [portalId]);
  const [form, setForm] = useState({
    portalId: portalId ?? '',
    type: 'renewal',
    planId: '',
    amount: '',
    method: 'cash',
    date: today(),
    reference: '',
  });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const planList = (plans.data?.plans ?? []).filter((p) => p.status === 'active' && !p.isTrial);
  const portalList = (portals.data?.portals ?? []).filter((p) => p.status !== 'archived');
  const planNameOf = (id) =>
    portalId ? currentPlan : portalList.find((p) => p.id === id)?.plan ?? null;

  /** What the ladder says this is worth — a starting point, still editable. */
  function priceFor(next) {
    const plan =
      next.type === 'upgrade'
        ? planList.find((p) => p.id === next.planId)
        : planList.find((p) => p.name === planNameOf(next.portalId));
    return plan ? String(plan.price) : '';
  }

  function update(patch) {
    setForm((f) => {
      const next = { ...f, ...patch };
      // Re-price only when the SELECTION moves, so a typed amount survives a
      // change of method or date.
      if ('portalId' in patch || 'type' in patch || 'planId' in patch) {
        next.amount = priceFor(next);
      }
      return next;
    });
  }

  /* Opened from a portal's own screen, the portal is already chosen — but the
     ladder price is not known until the plans land, so the renewal amount is
     filled here rather than in the initial state. Only while the box is still
     empty: a typed amount is never overwritten. */
  const plansReady = Boolean(plans.data);
  useEffect(() => {
    if (!plansReady || !portalId) return;
    setForm((f) => (f.amount === '' ? { ...f, amount: priceFor(f) } : f));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plansReady, portalId]);

  async function save() {
    if (busy) return;
    if (!form.portalId) return setError('Which portal paid you?');
    if (form.type === 'upgrade' && !form.planId) return setError('Choose the plan they moved to.');
    const amount = Number(form.amount);
    if (!(amount > 0)) return setError('Enter the amount you received.');
    setBusy(true);
    setError(null);
    try {
      await platform.sa.recordPayment(form.portalId, {
        type: form.type,
        planId: form.type === 'upgrade' ? form.planId : undefined,
        amount,
        method: form.method,
        date: form.date,
        reference: form.reference.trim() || undefined,
      });
      onDone();
    } catch (err) {
      setError(err?.message ?? 'Could not record that payment.');
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink-900/70 p-4 backdrop-blur-[2px] motion-safe:animate-fadein" role="dialog">
      <BackToClose onClose={onClose} />
      <div className="max-h-[92vh] w-full max-w-sm overflow-auto border border-line bg-white p-5 shadow-[0_40px_80px_-40px_rgba(11,15,24,.55)]">
        <p className="font-market text-[17px] font-extrabold tracking-[-0.02em] text-ink-700">
          Record a payment
        </p>
        <p className="mb-4 mt-1 text-sm leading-relaxed text-copy-muted">
          Money that reached you outside the app — cash, or a transfer you can already see.
          {portalName ? ` ${portalName}'s ` : ' The '}plan changes as soon as you save, and the
          owner is emailed the confirmation.
        </p>

        {!portalId && (
          <Field label="Portal" required>
            <select
              className={INPUT_CLS}
              value={form.portalId}
              onChange={(e) => update({ portalId: e.target.value })}
            >
              <option value="">Choose…</option>
              {portalList.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}{p.plan ? ` — ${p.plan}` : ''}
                </option>
              ))}
            </select>
          </Field>
        )}

        <Field label="What for" required>
          <div className="flex gap-2">
            {['renewal', 'upgrade'].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => update({ type: t })}
                className={`flex-1 border px-3 py-2 font-market text-xs uppercase ${
                  form.type === t ? 'border-gold-500 bg-gold-50' : 'border-line-strong'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </Field>

        {form.type === 'upgrade' && (
          <Field label="Plan they moved to" required>
            <select
              className={INPUT_CLS}
              value={form.planId}
              onChange={(e) => update({ planId: e.target.value })}
            >
              <option value="">Choose…</option>
              {planList.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} — {p.seats} seats · {formatMoney(p.price)}
                </option>
              ))}
            </select>
          </Field>
        )}

        <Row2>
          <Field label="Amount (₹)" required>
            <input
              className={INPUT_CLS}
              type="number"
              value={form.amount}
              onChange={(e) => update({ amount: e.target.value })}
            />
          </Field>
          <Field label="How" required>
            <select
              className={INPUT_CLS}
              value={form.method}
              onChange={(e) => update({ method: e.target.value })}
            >
              {METHODS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
        </Row2>

        <Row2>
          <Field label="Received on" required>
            <input
              className={INPUT_CLS}
              type="date"
              max={today()}
              value={form.date}
              onChange={(e) => update({ date: e.target.value })}
            />
          </Field>
          <Field label="Reference">
            <input
              className={INPUT_CLS}
              value={form.reference}
              onChange={(e) => update({ reference: e.target.value })}
              placeholder="UTR / note"
            />
          </Field>
        </Row2>

        {error && <FormError>{error}</FormError>}
        <div className="mt-4 flex justify-end gap-2">
          <Button small onClick={onClose}>Cancel</Button>
          <Button small tone="p" disabled={busy} onClick={save}>
            {busy ? 'Recording…' : 'Record & apply'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- plans -- */

export function SAPlans() {
  const state = useLoad(() => platform.sa.plans());
  const [draft, setDraft] = useState(null); // { id?, name, seats, price }
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      if (draft.id) await platform.sa.updatePlan(draft.id, draft);
      else await platform.sa.createPlan(draft);
      setDraft(null);
      state.reload();
    } catch (err) {
      setError(err?.message ?? 'Could not save the plan.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <LoadGate state={state}>
      {(d) => (
        <>
          <Eyebrow right={<Button small tone="p" onClick={() => setDraft({ name: '', seats: '', price: '' })}>New plan</Button>}>
            Subscription plans
          </Eyebrow>
          <Table head={th(['Plan', 'Seats', 'Price / yr', 'Term', 'Status', ''])}>
            {d.plans.map((p) => (
              <tr key={p.id}>
                <td className="font-semibold text-ink-700">
                  {p.name}
                  {p.isTrial && (
                    <span className="ml-2 align-middle"><Pill tone="sent">free trial</Pill></span>
                  )}
                </td>
                <td className="font-market tabular-nums">{p.seats.toLocaleString('en-IN')}</td>
                <td className="font-market tabular-nums">{formatMoney(p.price)}</td>
                <td className="font-market tabular-nums">{p.months} mo</td>
                <td><Pill tone={p.status === 'active' ? 'ok' : 'grey'}>{p.status}</Pill></td>
                <td className="text-right">
                  <Button small onClick={() => setDraft({ id: p.id, name: p.name, seats: p.seats, price: p.price })}>
                    Edit
                  </Button>{' '}
                  <Button
                    small
                    onClick={() =>
                      platform.sa
                        .updatePlan(p.id, { status: p.status === 'active' ? 'archived' : 'active' })
                        .then(state.reload)
                    }
                  >
                    {p.status === 'active' ? 'Archive' : 'Restore'}
                  </Button>
                </td>
              </tr>
            ))}
          </Table>

          {draft && (
            <div className="fixed inset-0 z-50 grid place-items-center bg-ink-900/70 p-4 backdrop-blur-[2px] motion-safe:animate-fadein" role="dialog">
              <BackToClose onClose={() => setDraft(null)} />
              <div className="w-full max-w-sm border border-line bg-white p-5 shadow-[0_40px_80px_-40px_rgba(11,15,24,.55)]">
                <p className="mb-4 font-market text-[17px] font-extrabold tracking-[-0.02em] text-ink-700">
                  {draft.id ? 'Edit plan' : 'New plan'}
                </p>
                <Field label="Name" required>
                  <input className={INPUT_CLS} value={draft.name} onChange={(e) => setDraft((f) => ({ ...f, name: e.target.value }))} />
                </Field>
                <Field label="Seat capacity" required>
                  <input className={INPUT_CLS} type="number" value={draft.seats} onChange={(e) => setDraft((f) => ({ ...f, seats: Number(e.target.value) }))} />
                </Field>
                <Field label="Price per year (₹)" required>
                  <input className={INPUT_CLS} type="number" value={draft.price} onChange={(e) => setDraft((f) => ({ ...f, price: Number(e.target.value) }))} />
                </Field>
                {error && <FormError>{error}</FormError>}
                <div className="mt-4 flex justify-end gap-2">
                  <Button small onClick={() => setDraft(null)}>Cancel</Button>
                  <Button small tone="p" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </LoadGate>
  );
}

/* ------------------------------------------------------------- payments -- */

export function SAPayments() {
  const state = useLoad(() => platform.sa.payments());
  const [error, setError] = useState(null);
  // A native prompt() here froze the whole tab in testing — every input in
  // this console is a proper dialog.
  const [dialog, setDialog] = useState(null); // { kind: 'mark'|'reject', payment, value }
  const [proofOpen, setProofOpen] = useState(null); // a payment whose screenshot is open
  const [recording, setRecording] = useState(false); // money received outside the app
  const [onlyAwaiting, setOnlyAwaiting] = useState(false);
  const [busy, setBusy] = useState(false);

  async function act(fn) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await fn();
      setDialog(null);
      state.reload();
    } catch (err) {
      setError(err?.message ?? 'Could not do that.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <LoadGate state={state}>
      {(d) => {
        const awaiting = d.payments.filter((p) => p.status === 'paid').length;
        const rows = onlyAwaiting ? d.payments.filter((p) => p.status === 'paid') : d.payments;
        return (
          <>
            <Eyebrow
              right={
                <button
                  type="button"
                  onClick={() => setOnlyAwaiting((v) => !v)}
                  data-label={`Awaiting verification · ${awaiting}`}
                  className={`text-stable border px-3 py-1.5 font-market text-[11px] uppercase tracking-[0.1em] transition-colors ${
                    onlyAwaiting ? 'border-gold-500 bg-gold-50 font-bold text-ink-700' : 'border-line-strong text-copy-muted hover:text-ink-700'
                  }`}
                >
                  Awaiting verification · {awaiting}
                </button>
              }
            >
              Platform payments
            </Eyebrow>
            <p className="mb-3 text-xs text-copy-label">
              A portal owner raises a request, pays the account on your Bank
              details screen and uploads a screenshot — that row reads{' '}
              <b>awaiting verification</b>. Check it against the statement, then
              verify (the plan changes at once) or reject with a reason they read.
              Paid you in cash or by a transfer you can already see? Record it
              here instead — it books the money and applies the plan at once.
            </p>
            <div className="mb-3">
              <Button small tone="p" onClick={() => { setRecording(true); setError(null); }}>
                + Record a payment
              </Button>
            </div>
            {error && !dialog && <FormError>{error}</FormError>}
            {rows.length === 0 ? (
              <Empty title={onlyAwaiting ? 'Nothing to verify' : 'No payments yet'} />
            ) : (
              <Table head={th(['Portal', 'For', 'Amount', 'Proof', 'Status', ''])}>
                {rows.map((p) => (
                  <tr key={p.id}>
                    {/* ONE wrapper per cell: on a phone the cell is a flex row,
                        and loose children would sit as separate items. */}
                    <td>
                      <span className="block">
                        <span className="font-semibold text-ink-700">{p.portalName ?? '—'}</span>
                        <span className="block font-market text-[10px] uppercase tracking-[0.1em] text-copy-label">
                          {p.requestedBy === 'super_admin' ? 'raised by you' : 'raised by the owner'} ·{' '}
                          {new Date(p.createdAt).toLocaleDateString('en-IN')}
                        </span>
                      </span>
                    </td>
                    <td>
                      <span className="block">
                        {p.type === 'upgrade' && p.targetPlan ? `${p.targetPlan} plan` : p.type}
                        {/* How it arrived, when anyone said. A blank cell means
                            the transfer-with-proof path — the screenshot column
                            is the record there. */}
                        {p.method && (
                          <span className="block font-market text-[10px] uppercase tracking-[0.1em] text-copy-label">
                            {METHOD_LABEL[p.method] ?? p.method}
                          </span>
                        )}
                        {p.reference && (
                          <span className="block font-market text-[11px] tabular-nums text-copy-muted">
                            ref {p.reference}
                          </span>
                        )}
                        {p.status === 'failed' && p.rejectionReason && (
                          <span className="block text-[11px] text-flag-600">{p.rejectionReason}</span>
                        )}
                      </span>
                    </td>
                    <td className="font-market tabular-nums">{formatMoney(p.amount)}</td>
                    <td>
                      {p.proofUrl ? (
                        <button type="button" onClick={() => setProofOpen(p)} title="Open the screenshot">
                          <img src={p.proofUrl} alt="Payment screenshot" className="h-12 w-9 border border-line object-cover" />
                        </button>
                      ) : (
                        <span className="text-copy-label">—</span>
                      )}
                    </td>
                    <td><Pill tone={PAYMENT_TONE[p.status]}>{PAYMENT_LABEL[p.status] ?? p.status}</Pill></td>
                    <td className="space-x-1 whitespace-nowrap text-right">
                      {(p.status === 'requested' || p.status === 'link_sent') && (
                        <Button small onClick={() => { setDialog({ kind: 'mark', payment: p, value: '', method: 'bank' }); setError(null); }}>
                          Mark paid
                        </Button>
                      )}
                      {p.status !== 'verified' && p.status !== 'failed' && p.status !== 'cancelled' && (
                        <>
                          <Button small tone="p" disabled={busy} onClick={() => act(() => platform.sa.verifyPayment(p.id))}>
                            Verify
                          </Button>
                          <Button small tone="r" onClick={() => { setDialog({ kind: 'reject', payment: p, value: '' }); setError(null); }}>
                            Reject
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </Table>
            )}

            {recording && (
              <RecordPaymentDialog
                onClose={() => setRecording(false)}
                onDone={() => {
                  setRecording(false);
                  state.reload();
                }}
              />
            )}

            {proofOpen && (
              <div
                className="fixed inset-0 z-50 grid place-items-center bg-ink-900/80 p-4 backdrop-blur-[2px] motion-safe:animate-fadein"
                role="dialog"
                onMouseDown={(e) => e.target === e.currentTarget && setProofOpen(null)}
              >
                <BackToClose onClose={() => setProofOpen(null)} />
                <div className="max-h-[92vh] w-full max-w-md overflow-auto border border-line bg-white shadow-[0_40px_80px_-40px_rgba(11,15,24,.55)]">
                  <div className="flex items-start justify-between gap-3 border-b border-line p-4">
                    <div>
                      <p className="font-semibold text-ink-700">{proofOpen.portalName} · {formatMoney(proofOpen.amount)}</p>
                      <p className="font-market text-xs text-copy-label">
                        {proofOpen.reference ? `ref ${proofOpen.reference} · ` : ''}
                        uploaded {proofOpen.proofUploadedAt ? new Date(proofOpen.proofUploadedAt).toLocaleString('en-IN') : '—'}
                      </p>
                    </div>
                    <Button small onClick={() => setProofOpen(null)}>Close</Button>
                  </div>
                  {/* Capped so Verify / Reject stay on screen under a tall phone
                      screenshot; the link opens the untouched original. */}
                  <a href={proofOpen.proofUrl} target="_blank" rel="noreferrer" title="Open the original">
                    <img src={proofOpen.proofUrl} alt="Payment screenshot" className="max-h-[56vh] w-full bg-ground object-contain" />
                  </a>
                  {proofOpen.status === 'paid' && (
                    <div className="flex justify-end gap-2 border-t border-line p-4">
                      <Button small tone="r" disabled={busy} onClick={() => { setProofOpen(null); setDialog({ kind: 'reject', payment: proofOpen, value: '' }); }}>
                        Reject
                      </Button>
                      <Button small tone="p" disabled={busy} onClick={() => { setProofOpen(null); act(() => platform.sa.verifyPayment(proofOpen.id)); }}>
                        Verify payment
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {dialog && (
              <div className="fixed inset-0 z-50 grid place-items-center bg-ink-900/70 p-4 backdrop-blur-[2px] motion-safe:animate-fadein" role="dialog">
                <BackToClose onClose={() => setDialog(null)} />
                <div className="w-full max-w-sm border border-line bg-white p-5 shadow-[0_40px_80px_-40px_rgba(11,15,24,.55)]">
                  <p className="font-market text-[17px] font-extrabold tracking-[-0.02em] text-ink-700">
                    {dialog.kind === 'mark' ? 'Mark paid' : 'Reject this payment'}
                  </p>
                  <p className="mb-4 mt-1 text-sm text-copy-muted">
                    {dialog.payment.portalName ?? 'Portal'} · {formatMoney(dialog.payment.amount)}
                  </p>
                  {dialog.kind === 'mark' ? (
                    <>
                      <Field label="How the money arrived" required>
                        <select
                          className={INPUT_CLS}
                          value={dialog.method}
                          onChange={(e) => setDialog((m) => ({ ...m, method: e.target.value }))}
                        >
                          {METHODS.map(([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Payment reference (UTR / note)">
                        <input
                          className={INPUT_CLS}
                          value={dialog.value}
                          onChange={(e) => setDialog((m) => ({ ...m, value: e.target.value }))}
                        />
                      </Field>
                    </>
                  ) : (
                    <Field label="Reason — the portal owner reads this" required>
                      <textarea
                        className={TEXTAREA_CLS}
                        value={dialog.value}
                        onChange={(e) => setDialog((m) => ({ ...m, value: e.target.value }))}
                        placeholder="e.g. No transfer of this amount has reached the account."
                      />
                    </Field>
                  )}
                  {error && <FormError>{error}</FormError>}
                  <div className="mt-4 flex justify-end gap-2">
                    <Button small onClick={() => setDialog(null)}>Cancel</Button>
                    <Button
                      small
                      tone={dialog.kind === 'mark' ? 'p' : 'r'}
                      disabled={busy}
                      onClick={() =>
                        act(() =>
                          dialog.kind === 'mark'
                            ? platform.sa.markPaid(
                                dialog.payment.id,
                                dialog.value,
                                undefined,
                                dialog.method,
                              )
                            : platform.sa.rejectPayment(dialog.payment.id, dialog.value.trim()),
                        )
                      }
                    >
                      {busy ? 'Saving…' : dialog.kind === 'mark' ? 'Mark paid' : 'Reject payment'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        );
      }}
    </LoadGate>
  );
}

/* --------------------------------------------------------- bank details -- */

/**
 * Where portal owners pay their subscription (owner's direction, 6 Sep 2026).
 * The platform twin of a club's "How members pay you": holder, account,
 * IFSC, UPI id, an instruction line and a QR. Every portal's Subscription
 * tab prints exactly this the moment a payment request is open, so a blank
 * screen here means nobody can pay you.
 */
export function SABankDetails() {
  const state = useLoad(() => platform.sa.bankDetails());
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  function edit(bank) {
    setForm({
      upi: bank.upi ?? '',
      holder: bank.holder ?? '',
      account: bank.account ?? '',
      ifsc: bank.ifsc ?? '',
      details: bank.details ?? '',
      /* Either the stored /api/images URL or a fresh data URL, until saved. */
      qr: bank.qrUrl ?? null,
    });
    setError(null);
    setSaved(false);
  }

  async function pickQr() {
    try {
      setForm((f) => ({ ...f, qr: null, pending: true }));
      const qr = await pickAndResizePhoto(QR_IMAGE);
      setForm((f) => ({ ...f, qr, pending: false }));
    } catch (err) {
      setForm((f) => ({ ...f, pending: false }));
      setError(err instanceof Error ? err.message : 'Could not read that image');
    }
  }

  async function save() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const qrUrl = form.qr && form.qr.startsWith('data:') ? await uploadImage(form.qr) : (form.qr ?? null);
      await platform.sa.setBankDetails({
        upi: form.upi,
        holder: form.holder,
        account: form.account,
        ifsc: form.ifsc,
        details: form.details,
        qrUrl,
      });
      setForm(null);
      setSaved(true);
      state.reload();
    } catch (err) {
      setError(err?.message ?? 'Could not save.');
    } finally {
      setBusy(false);
    }
  }

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <LoadGate state={state}>
      {({ bank }) => (
        <>
          <Eyebrow
            right={
              !form && (
                <Button small tone={bank.configured ? 'g' : 'p'} onClick={() => edit(bank)}>
                  {bank.configured ? 'Change' : 'Set up'}
                </Button>
              )
            }
          >
            How portals pay you
          </Eyebrow>
          <p className="mb-3 text-xs text-copy-label">
            Printed on every portal&rsquo;s Subscription tab while a payment request
            is open. The owner pays this account or QR, uploads a screenshot,
            and the row lands on your Payments screen to verify.
          </p>
          {saved && !form && <Hint tone="ok">Bank details saved.</Hint>}

          {!form ? (
            <div className="flex flex-wrap items-start gap-4 border border-line bg-white p-4">
              {bank.qrUrl ? (
                <img src={bank.qrUrl} alt="Platform payment QR" className="h-[104px] w-[104px] flex-none border border-line object-contain" />
              ) : (
                <div className="grid h-[104px] w-[104px] flex-none place-items-center border border-dashed border-line-strong bg-ground text-center font-market text-[9.5px] leading-tight text-copy-label">
                  No QR
                  <br />
                  yet
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="mb-1.5">
                  <Pill tone={bank.configured ? 'ok' : 'late'}>
                    {bank.configured ? 'QR / bank transfer' : 'Nowhere to pay yet'}
                  </Pill>
                </div>
                {bank.upi && (
                  <p className="font-market text-[15px] font-bold tracking-[-0.01em] text-ink-700">{bank.upi}</p>
                )}
                {(bank.holder || bank.account || bank.ifsc) && (
                  <p className="mt-1 font-market text-[12.5px] leading-[1.7] tabular-nums text-copy">
                    {bank.holder && <>{bank.holder}<br /></>}
                    {bank.account && <>A/C {bank.account}</>}
                    {bank.account && bank.ifsc && ' · '}
                    {bank.ifsc && <>IFSC {bank.ifsc}</>}
                  </p>
                )}
                {bank.details && (
                  <p className="mt-1 whitespace-pre-line text-[12px] leading-relaxed text-copy-muted">{bank.details}</p>
                )}
                {!bank.configured && (
                  <p className="text-[12.5px] leading-relaxed text-copy-muted">
                    A portal owner who requests a plan is told what they owe but
                    has no account to send it to. Add a UPI id, a QR, or an
                    account number with its IFSC.
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="border border-line bg-white p-5">
              <Field label="UPI id">
                <input className={INPUT_CLS} value={form.upi} onChange={set('upi')} placeholder="runnercrew@okhdfcbank" />
              </Field>
              <Field label="Account holder name">
                <input className={INPUT_CLS} value={form.holder} onChange={set('holder')} placeholder="RunnerCrew" />
              </Field>
              <Row2>
                <Field label="Account number">
                  <input
                    className={INPUT_CLS}
                    value={form.account}
                    onChange={(e) => setForm((f) => ({ ...f, account: e.target.value.replace(/\D/g, '') }))}
                    inputMode="numeric"
                    placeholder="501000123456789"
                  />
                </Field>
                <Field label="IFSC code">
                  <input
                    className={INPUT_CLS}
                    value={form.ifsc}
                    onChange={(e) => setForm((f) => ({ ...f, ifsc: e.target.value.toUpperCase() }))}
                    placeholder="HDFC0001234"
                    maxLength={11}
                  />
                </Field>
              </Row2>
              <Field label="Instructions for the payer">
                <textarea
                  className={TEXTAREA_CLS}
                  value={form.details}
                  onChange={set('details')}
                  placeholder="Put your portal name in the payment note so we can match it."
                />
              </Field>

              <div className="mb-4">
                <div className="mb-2 font-market text-[10px] font-bold uppercase tracking-[0.16em] text-copy-label">
                  Payment QR
                </div>
                <div className="flex items-center gap-4 border border-line bg-ground p-3.5">
                  {form.qr ? (
                    <img src={form.qr} alt="Platform payment QR" className="h-[92px] w-[92px] flex-none border border-line bg-white object-contain" />
                  ) : (
                    <div className="grid h-[92px] w-[92px] flex-none place-items-center border border-dashed border-line-strong bg-white text-center font-market text-[9.5px] leading-tight text-copy-label">
                      {form.pending ? 'Reading…' : <>No QR<br />yet</>}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-[12.5px] leading-relaxed text-copy-muted">
                      Screenshot the QR from your bank or UPI app and upload it.
                      Portal owners scan it straight off their Subscription tab.
                    </p>
                    <div className="mt-2.5 flex gap-2">
                      <Button tone="p" small onClick={() => void pickQr()}>
                        {form.qr ? 'Replace' : 'Upload QR'}
                      </Button>
                      {form.qr && (
                        <Button tone="g" small onClick={() => setForm((f) => ({ ...f, qr: null }))}>
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <Hint>
                Every portal owner with an open request can see this. Put the
                platform&rsquo;s receiving account here — never a personal one.
              </Hint>
              {error && <FormError>{error}</FormError>}
              <div className="mt-4 flex justify-end gap-2">
                <Button small onClick={() => setForm(null)}>Cancel</Button>
                <Button small tone="p" onClick={save} disabled={busy}>
                  {busy ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </LoadGate>
  );
}

/* ------------------------------------------------------- trace & emails -- */

export function SATrace() {
  const state = useLoad(() => platform.sa.trace());
  return (
    <LoadGate state={state}>
      {(d) => (
        <>
          <Eyebrow right={d.entries.length}>Trace log</Eyebrow>
          {d.entries.length === 0 ? (
            <Empty title="Nothing logged yet" />
          ) : (
            <Table head={th(['When', 'Portal', 'Actor', 'Action', 'Seats'])}>
              {d.entries.map((e) => (
                <tr key={e.id}>
                  <td className="whitespace-nowrap font-market text-xs tabular-nums">{new Date(e.at).toLocaleString('en-IN')}</td>
                  <td>{e.portal ?? <span className="text-copy-label">platform</span>}</td>
                  <td>{e.actor ?? '—'}</td>
                  <td className="font-market text-xs">{e.action}</td>
                  <td className="font-market tabular-nums">{e.seatsAffected ?? ''}</td>
                </tr>
              ))}
            </Table>
          )}
        </>
      )}
    </LoadGate>
  );
}

export function SAEmails() {
  const state = useLoad(() => platform.sa.emails());
  const [open, setOpen] = useState(null);
  const MAIL_TONE = { queued: 'pend', sent: 'ok', delivered: 'ok', bounced: 'late', complained: 'late', failed: 'late' };
  // A dev-mode row is written status = 'sent' and was delivered to nobody.
  // Showing it as a green "sent" is the difference between believing mail
  // works and knowing it does — so say what actually happened instead.
  const isDevMode = (e) => e.providerMessageId === 'dev-mode';
  const mailLabel = (e) => (isDevMode(e) ? 'not delivered' : e.status);
  const mailTone = (e) => (isDevMode(e) ? 'late' : (MAIL_TONE[e.status] ?? 'grey'));
  return (
    <LoadGate state={state}>
      {(d) => (
        <>
          <Eyebrow right={d.emails.length}>Mail outbox</Eyebrow>
          <p className="mb-3 text-xs text-copy-label">
            Every message the platform queued. <b>Not delivered</b> means dev
            mode — no Resend key, so it was recorded and sent to nobody. A real
            send carries a Resend id; open a row to see it, or to read the
            message with its links.
          </p>
          {d.emails.length === 0 ? (
            <Empty title="Nothing sent yet" />
          ) : (
            <Table head={th(['Queued', 'To', 'Type', 'Subject', 'Status'])}>
              {d.emails.map((e) => (
                <tr key={e.id} className="cursor-pointer hover:bg-ground-warm" onClick={() => setOpen(e)}>
                  <td className="whitespace-nowrap font-market text-xs tabular-nums">{new Date(e.queuedAt).toLocaleString('en-IN')}</td>
                  <td className="font-market text-xs">{e.to}</td>
                  <td className="font-market text-xs">{e.type}</td>
                  <td>{e.subject}</td>
                  <td><Pill tone={mailTone(e)}>{mailLabel(e)}</Pill></td>
                </tr>
              ))}
            </Table>
          )}
          {open && (
            <div className="fixed inset-0 z-50 grid place-items-center bg-ink-900/70 p-4 backdrop-blur-[2px] motion-safe:animate-fadein" role="dialog" onMouseDown={(e) => e.target === e.currentTarget && setOpen(null)}>
              <BackToClose onClose={() => setOpen(null)} />
              <div className="max-h-[85vh] w-full max-w-xl overflow-auto border border-line bg-white shadow-[0_40px_80px_-40px_rgba(11,15,24,.55)]">
                <div className="border-b border-line p-4">
                  <p className="font-semibold text-ink-700">{open.subject}</p>
                  <p className="font-market text-xs text-copy-label">
                    to {open.to} · {mailLabel(open)}
                    {open.providerMessageId && !isDevMode(open) ? ` · resend id ${open.providerMessageId}` : ''}
                    {open.lastError ? ` · ${open.lastError}` : ''}
                  </p>
                </div>
                {/* Rendered in a sandboxed iframe — this is our own template HTML. */}
                <iframe title="mail" srcDoc={open.html} sandbox="" className="h-[60vh] w-full" />
              </div>
            </div>
          )}
        </>
      )}
    </LoadGate>
  );
}

/* -------------------------------------------------------------- reports -- */

export function SAReports() {
  const state = useLoad(() => platform.sa.reports());
  return (
    <LoadGate state={state}>
      {(d) => (
        <>
          <Eyebrow>Revenue by month</Eyebrow>
          {d.revenueByMonth.length === 0 ? (
            <Empty title="No verified payments yet" />
          ) : (
            <Table head={th(['Month', 'Verified revenue'])}>
              {d.revenueByMonth.map((r) => (
                <tr key={r.month}>
                  <td className="font-market tabular-nums">{r.month}</td>
                  <td className="font-market tabular-nums">{formatMoney(r.total)}</td>
                </tr>
              ))}
            </Table>
          )}

          <div className="mt-8">
            <Eyebrow>Seat utilisation</Eyebrow>
            <Table head={th(['Portal', 'Used', 'Capacity', '%'])}>
              {d.utilisation.map((u) => (
                <tr key={u.portalId}>
                  <td className="font-semibold text-ink-700">{u.portalName}</td>
                  <td className="font-market tabular-nums">{u.used}</td>
                  <td className="font-market tabular-nums">{u.capacity}</td>
                  <td className="font-market tabular-nums">
                    {u.capacity ? Math.round((u.used / u.capacity) * 100) : 0}%
                  </td>
                </tr>
              ))}
            </Table>
          </div>

          <div className="mt-8">
            <Eyebrow>Expiring within two months</Eyebrow>
            {d.upcomingExpiries.length === 0 ? (
              <Empty title="Nothing on the horizon" />
            ) : (
              <Table head={th(['Portal', 'Ends'])}>
                {d.upcomingExpiries.map((e, i) => (
                  <tr key={i}>
                    <td>{e.portalName}</td>
                    <td className="font-market tabular-nums">{formatDate(e.endDate)}</td>
                  </tr>
                ))}
              </Table>
            )}
          </div>
        </>
      )}
    </LoadGate>
  );
}
